import { FC, useCallback, useEffect, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { StanToggle } from '@/components';
import { TagSelector } from '@/features/email-flows/components/audience/TagSelector';
import { useTagManagement } from '@/features/email-flows/queries';
import { buildProducts, ProductOption } from '@/lib/helpers';
import type { BroadcastFormData } from '../validations/broadcastForm';
import { cn } from '@/lib';
import { useBroadcastEditorStore } from '../stores';
import { useDefaultStore } from '@/stores/defaultStore';
import type { Tag } from '@/features/email-flows/types/audience.types';
import { useStoreStore } from '@/stores';

interface BroadcastAudienceSectionProps {
    setValue: UseFormSetValue<BroadcastFormData>;
    disabled: boolean;
    isFetchingCount?: boolean;
}

export const BroadcastAudienceSection: FC<BroadcastAudienceSectionProps> = ({
    setValue,
    disabled,
    isFetchingCount = false,
}) => {
    const { store } = useDefaultStore();
    const getFunnels = useStoreStore(state => state.getFunnels);

    const selectedTags = useBroadcastEditorStore(state => state.selectedTags);
    const selectedExcludedTags = useBroadcastEditorStore(state => state.selectedExcludedTags);
    const recipientCount = useBroadcastEditorStore(state => state.recipientCount);
    const setSelectedTags = useBroadcastEditorStore(state => state.setSelectedTags);
    const setSelectedExcludedTags = useBroadcastEditorStore(state => state.setSelectedExcludedTags);

    const [excludeUsersToggle, setExcludeUsersToggle] = useState(false);

    const funnels = store ? getFunnels() : [];

    const productOptions = store
        ? (buildProducts({
              store,
              funnels,
              format: 'tags',
          }) as ProductOption[])
        : [];

    useEffect(() => {
        if (selectedExcludedTags.length > 0 && !excludeUsersToggle) {
            setExcludeUsersToggle(true);
        }
    }, [selectedExcludedTags.length, excludeUsersToggle]);

    const handleIncludeTagsChange = useCallback(
        (tags: Tag[]) => {
            setSelectedTags(tags);
            setValue('tags', tags, { shouldDirty: true, shouldValidate: false });
        },
        [setValue, setSelectedTags]
    );

    const handleExcludeTagsChange = useCallback(
        (tags: Tag[]) => {
            setSelectedExcludedTags(tags);
            setValue('excludedTags', tags, { shouldDirty: true, shouldValidate: false });
        },
        [setValue, setSelectedExcludedTags]
    );

    const includeTagManagement = useTagManagement({
        mode: 'include',
        selectedTags,
        onTagsChange: handleIncludeTagsChange,
        productOptions,
    });

    const excludeTagManagement = useTagManagement({
        mode: 'exclude',
        selectedTags: selectedExcludedTags,
        onTagsChange: handleExcludeTagsChange,
        productOptions,
    });

    return (
        <div className='border-subtle space-y-4 border-b pb-4'>
            <TagSelector
                mode='include'
                selectedTags={selectedTags}
                availableTags={includeTagManagement.availableTags}
                onTagAdd={includeTagManagement.handleTagAdd}
                onTagRemove={includeTagManagement.handleTagRemove}
                placeholder='Select tags'
                disabled={disabled}
                label={
                    !disabled ? (
                        <div className='flex items-center justify-between'>
                            <p className='text-label-xs-bold'>Tags</p>
                            <div className='flex items-center gap-1.5'>
                                <p className='text-gray text-label-xs-regular'>{recipientCount} Recipients</p>
                                <div
                                    className={cn('overflow-hidden transition-all duration-200', {
                                        'w-2.5': isFetchingCount,
                                        'w-0': !isFetchingCount,
                                    })}
                                >
                                    <div className='border-primary size-2.5 animate-spin rounded-full border border-t-transparent' />
                                </div>
                            </div>
                        </div>
                    ) : (
                        'Tags'
                    )
                }
            />

            <div className='flex items-center gap-2'>
                <StanToggle
                    id='exclude-users-toggle'
                    checked={excludeUsersToggle}
                    onChange={e => {
                        setExcludeUsersToggle(e.target.checked);
                        if (!e.target.checked) {
                            setSelectedExcludedTags([]);
                            setValue('excludedTags', [], {
                                shouldDirty: true,
                                shouldValidate: false,
                            });
                        }
                    }}
                    disabled={disabled}
                    toggleSize='sm'
                />
                <label
                    htmlFor='exclude-users-toggle'
                    className={cn(
                        'text-label-xs-regular! font-medium',
                        disabled ? 'text-gray cursor-not-allowed' : 'cursor-pointer'
                    )}
                >
                    Exclude Users
                </label>
            </div>

            {excludeUsersToggle && (
                <TagSelector
                    mode='exclude'
                    selectedTags={selectedExcludedTags}
                    availableTags={excludeTagManagement.availableTags}
                    onTagAdd={excludeTagManagement.handleTagAdd}
                    onTagRemove={excludeTagManagement.handleTagRemove}
                    placeholder="Don't send to..."
                    disabled={disabled}
                />
            )}
        </div>
    );
};
